import { useEffect, useState } from 'preact/hooks';
import { clearStorage, loadStorage } from './core/storage';
import { resolveFabPosition } from './core/fab';
import { connect, disconnect } from './core/transport';
import { ChannelPicker } from './components/ChannelPicker';
import { ChatActive } from './components/ChatActive';
import { FloatingButton } from './components/FloatingButton';
import { Lightbox } from './components/Lightbox';
import { PreChatForm } from './components/PreChatForm';
import { useMediaQuery } from './hooks/useMediaQuery';
import {
  consentGiven,
  resetChat,
  runtimeConfig,
  screen,
  unreadCount,
  userName,
} from './state/widget';
import type { WidgetBootConfig } from './types/public';

type Props = { config: WidgetBootConfig };

// Durations should match CSS keyframes
const CLOSE_MOBILE_MS = 240;
const CLOSE_DESKTOP_MS = 160;
const SWITCH_MOBILE_MS = 160;
const SWITCH_DESKTOP_MS = 140;

type Phase = 'idle' | 'closing' | 'switching';

export function App({ config }: Props) {
  const targetScreen = screen.value;
  const cfg = runtimeConfig.value;
  const isMobile = useMediaQuery('(max-width: 640px)');

  // displayScreen — что реально отрендерено в DOM (может отставать от targetScreen на время exit-анимации).
  const [displayScreen, setDisplayScreen] = useState(targetScreen);
  const [phase, setPhase] = useState<Phase>('idle');

  // Transition machinery: при смене target запускаем exit-анимацию текущего content,
  // после её окончания переключаем displayScreen → играется mount/fade-in нового.
  useEffect(() => {
    if (targetScreen === displayScreen) return;

    // Открытие из 'closed' — сразу отрендерить новый, пусть он играет свой entrance.
    if (displayScreen === 'closed') {
      setDisplayScreen(targetScreen);
      return;
    }

    const toClosed = targetScreen === 'closed';
    setPhase(toClosed ? 'closing' : 'switching');
    const delay = toClosed
      ? isMobile
        ? CLOSE_MOBILE_MS
        : CLOSE_DESKTOP_MS
      : isMobile
        ? SWITCH_MOBILE_MS
        : SWITCH_DESKTOP_MS;
    const t = window.setTimeout(() => {
      setDisplayScreen(targetScreen);
      setPhase('idle');
    }, delay);
    return () => window.clearTimeout(t);
  }, [targetScreen, displayScreen, isMobile]);

  const open = displayScreen !== 'closed';
  const isClosing = phase === 'closing';
  const isSwitching = phase === 'switching';
  // На мобиле: chat и form — fullscreen (там есть input'ы, и sheet
  // под клавиатурой не всплывает без vp-tracking, поэтому fullscreen);
  // picker — bottom sheet (без input'ов, клавиатура не появляется).
  const mobileVariant =
    isMobile && open
      ? displayScreen === 'picker'
        ? 'mobile mobile-sheet'
        : 'mobile mobile-full'
      : '';
  const fabPos = resolveFabPosition(
    isMobile ? config.fab.mobile : config.fab.desktop,
    cfg,
    isMobile ? 'mobile' : 'desktop',
  );
  const layerClass = `sw-root sw-layer sw-fab-${fabPos.side}${mobileVariant ? ` ${mobileVariant}` : ''}`;
  const zIndex = typeof cfg?.z_index === 'number' && Number.isFinite(cfg.z_index)
    ? cfg.z_index
    : null;
  const layerStyle = {
    '--cpw-fab-bottom': `${fabPos.bottom}px`,
    ...(zIndex !== null ? { '--cpw-z-index': String(zIndex) } : {}),
  } as preact.JSX.CSSProperties;
  const showFab = !(isMobile && open);
  const onSheet = isMobile && open && displayScreen === 'picker';

  // Никакой visualViewport-магии и body scroll lock'а. iOS 16+ сам
  // правильно сжимает 100dvh при появлении клавиатуры; iOS-zoom при
  // focus на input лечится font-size: 16px на самих input'ах (см. CSS).

  useEffect(() => {
    if (targetScreen !== 'chat') return;
    unreadCount.value = 0;
    const clientId = loadStorage(config.token).clientId;
    connect({
      apiUrl: config.apiUrl,
      wsPath: cfg?.ws_path,
      token: config.token,
      clientId,
      username: userName.value,
    });
    return () => {
      // Keep socket open when user minimizes to 'closed' — only disconnect on explicit reset
    };
  }, [targetScreen, config.apiUrl, config.token, cfg?.ws_path]);

  const close = () => {
    // Machinery в useEffect выше сама отыграет exit-анимацию и переключит displayScreen.
    screen.value = 'closed';
  };

  const hardReset = () => {
    // Мгновенный reset без анимации — dev-only инструмент.
    screen.value = 'closed';
    setDisplayScreen('closed');
    setPhase('idle');
    disconnect();
    // Сбрасываем identity ДО clearStorage, чтобы debounced effect в main.tsx
    // не перезаписал очищенный ключ старыми name/consent.
    userName.value = '';
    consentGiven.value = false;
    unreadCount.value = 0;
    resetChat();
    clearStorage(config.token, 'all');
  };

  const toggle = () => {
    if (open) {
      close();
    } else if (consentGiven.value) {
      screen.value = 'chat';
    } else {
      screen.value = 'picker';
    }
  };

  const onLayerClick = (e: MouseEvent) => {
    // Тап по затемнённому backdrop'у закрывает sheet.
    if (!onSheet || phase !== 'idle') return;
    const target = e.target as HTMLElement;
    if (target === e.currentTarget || target.classList.contains('sw-sheet-backdrop')) {
      close();
    }
  };

  // Transition-класс на корневом .sw-panel. На mobile-sheet — пусто, потому
  // что анимацию держит wrapper .sw-sheet (panel внутри fade только при
  // switching). На mobile-full и desktop — панель сама получает класс.
  const panelTransitionClass = (() => {
    if (onSheet) return isSwitching ? 'fading' : '';
    if (isClosing) return 'exiting';
    if (isSwitching) return 'fading';
    return '';
  })();

  const pickerNode =
    displayScreen === 'picker' ? (
      <ChannelPicker
        onClose={close}
        onWebChat={() => {
          screen.value = consentGiven.value ? 'chat' : 'form';
        }}
        telegramUrl={cfg?.telegram_url}
        whatsappUrl={cfg?.whatsapp_url}
        brand={cfg?.brand}
        panelClass={panelTransitionClass}
        isMobile={isMobile}
      />
    ) : null;

  const formNode =
    displayScreen === 'form' ? (
      <PreChatForm
        onBack={() => {
          screen.value = 'picker';
        }}
        onClose={close}
        initialName={userName.value}
        onSubmit={({ name }) => {
          userName.value = name;
          consentGiven.value = true;
          screen.value = 'chat';
        }}
        panelClass={panelTransitionClass}
        isMobile={isMobile}
      />
    ) : null;

  // Классы для wrapper'а/backdrop'а (только mobile-sheet).
  const sheetClass = `sw-sheet${isClosing ? ' exiting' : ''}`;
  const backdropClass = `sw-sheet-backdrop${isClosing ? ' exiting' : ''}`;

  return (
    <div class={layerClass} style={layerStyle} onClick={onLayerClick}>
      {onSheet ? (
        <>
          <div class={backdropClass} />
          <div class={sheetClass}>
            {pickerNode}
            {formNode}
          </div>
        </>
      ) : (
        <>
          {pickerNode}
          {formNode}
        </>
      )}

      {displayScreen === 'chat' ? (
        <ChatActive
          onClose={close}
          onBack={() => {
            screen.value = 'picker';
          }}
          isMobile={isMobile}
          panelClass={panelTransitionClass}
          token={config.token}
        />
      ) : null}

      {showFab && cfg && cfg.enabled !== false ? <FloatingButton open={open} onClick={toggle} /> : null}

      <Lightbox />

      {/* dev-only; не рендерится в prod */}
      {import.meta.env.DEV ? (
        <button
          type="button"
          onClick={hardReset}
          style={{
            position: 'fixed',
            right: 16,
            top: 16,
            padding: '4px 8px',
            fontSize: 10,
            opacity: 0.5,
            zIndex: 2147483647,
          }}
        >
          dev: reset
        </button>
      ) : null}
    </div>
  );
}
